import { MenuItem, Typography } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { mdiPencil, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import ActionContainer from 'components/ActionContainer/ActionContainer';
import Button from 'components/Button/Button';
import ButtonLoading from 'components/ButtonLoading/ButtonLoading';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import Dialog from 'components/DialogWrapper/DialogWrapper';
import TextInput from 'components/TextInput/TextInput';
import { E_INVOICE_OPERATORS } from 'constants/e-invoice-operators';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import uuidv4 from 'uuid/v4';
import AddressAutocomplete from '../AddressAutocomplete/AddressAutocomplete';
import useStyles from './CustomerDialogStyles';
import SwitchItem from './components/SwitchItem';
import { generateNewCustomerNumberByBusinessId } from 'store/customer/services/generateNewCustomerNumberByBusinessId.service';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { selectSelectedProject } from 'store/projects/selectors/projectsSelectors';
import { PAYMENT_TERM_OPTIONS } from 'constants/payment-term-options';

function CustomerDialogLayout(props) {
  const { customer, onClose, open, type, loading, onCreate, onUpdate } = props;
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation('common');

  const upSm = useMediaQuery(theme.breakpoints.up('sm'));

  const selectedProject = useSelector(selectSelectedProject);
  const businessId = selectedProject?.business_id;

  // Closable container open-states
  const [openContactInformation, setOpenContactInformation] = useState(true);
  const [openBillingInformation, setOpenBillingInformation] = useState(true);

  const [customerData, setCustomerData] = useState({
    // Customer information
    id: customer.id || uuidv4(),
    name: customer.name || '',
    number: customer.number || '',
    type: customer.type || 'business',
    customerBusinessId: customer.customerBusinessId || '',
    customerVatId: customer.customerVatId || '',
    email: customer.email || '',
    phone: customer.phone || '',
    additionalInfo: customer.additionalInfo || '',

    // Address
    street1: customer.street1 || '',
    street2: customer.street2 || '',
    co: customer.co || '',
    poBox: customer.poBox || '',
    postCode: customer.postCode || '',
    city: customer.city || '',
    country: customer.country || '',

    // Contact information
    contactFirstName: customer.contactFirstName || '',
    contactLastName: customer.contactLastName || '',
    contactEmail: customer.contactEmail || '',
    contactPhone: customer.contactPhone || '',

    // Billing information
    billingType: customer.billingType || 'eInvoice',
    paymentTerm: customer.paymentTerm || '',
    customerReferenceNumber: customer.customerReferenceNumber || '',
    reverseAlv: customer.reverseAlv || false,
    alvPercentage:
      customer.alvPercentage !== undefined ? customer.alvPercentage : 25.5,
    overheadPercentage: customer.overheadPercentage || 0,
    invoiceMessage: customer.invoiceMessage || '',

    // eInvoice
    eInvoiceAddress: customer.eInvoiceAddress || '',
    eInvoiceOperator: customer.eInvoiceOperator || '',

    // Billing email
    billingEmail: customer.billingEmail || '',

    // Billing address
    billingStreet1: customer.billingStreet1 || '',
    billingStreet2: customer.billingStreet2 || '',
    billingCo: customer.billingCo || '',
    billingPoBox: customer.billingPoBox || '',
    billingPostCode: customer.billingPostCode || '',
    billingCity: customer.billingCity || '',
    billingCountry: customer.billingCountry || '',
  });

  const handleChange = (e) => {
    if (!e) return;
    const { name, value } = e.target;

    setCustomerData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (businessId && type === 'create') {
      getNewCustomerNumber(businessId);
    }
  }, [businessId]);

  const getNewCustomerNumber = async (businessId) => {
    try {
      const result = await generateNewCustomerNumberByBusinessId(businessId);
      setCustomerData((prev) => ({
        ...prev,
        number: result.number,
      }));
    } catch (error) {
      console.error('Error generating new number:', error);
      enqueueSnackbar('Virhe numeron generoinnissa', {
        variant: 'error',
      });
    }
  };

  const compareContactAndBillingAddresses = () =>
    customerData.street1 && // Only compare if there's a street address
    ['street1', 'street2', 'poBox', 'co', 'postCode', 'city', 'country'].every(
      (key) =>
        customerData[key] ===
        customerData[`billing${key.charAt(0).toUpperCase() + key.slice(1)}`]
    );

  const [billingAddressSameAsContact, setBillingAddressSameAsContact] =
    useState(compareContactAndBillingAddresses() || false);

  const handleChangeContactStreet1 = (place) => {
    setCustomerData((prev) => {
      if (!place) {
        return {
          ...prev,
          street1: '',
          street2: '',
          poBox: '',
          co: '',
          postCode: '',
          city: '',
          country: '',
        };
      }

      return {
        ...prev,
        street1: place.Street
          ? `${place.Street} ${place.AddressNumber || ''}`.trim()
          : prev.street1,
        postCode: place.PostalCode || prev.postCode,
        city: place.Municipality || prev.city,
        country: place.Country === 'FIN' ? 'Suomi' : prev.country,
      };
    });
  };

  const handleChangeBillingStreet1 = (place) => {
    setCustomerData((prev) => {
      if (!place) {
        return {
          ...prev,
          billingStreet1: '',
          billingStreet2: '',
          billingPoBox: '',
          billingCo: '',
          billingPostCode: '',
          billingCity: '',
          billingCountry: '',
        };
      }

      return {
        ...prev,
        billingStreet1: place.Street
          ? `${place.Street} ${place.AddressNumber || ''}`.trim()
          : prev.billingStreet1,
        billingPostCode: place.PostalCode || prev.billingPostCode,
        billingCity: place.Municipality || prev.billingCity,
        billingCountry: place.Country === 'FIN' ? 'Suomi' : prev.billingCountry,
      };
    });
  };

  useEffect(() => {
    if (billingAddressSameAsContact) {
      setCustomerData((prev) => ({
        ...prev,
        billingStreet1: prev.street1,
        billingStreet2: prev.street2,
        billingPoBox: prev.poBox,
        billingCo: prev.co,
        billingPostCode: prev.postCode,
        billingCity: prev.city,
        billingCountry: prev.country,
      }));
    }
  }, [
    billingAddressSameAsContact,
    customerData.street1,
    customerData.street2,
    customerData.poBox,
    customerData.co,
    customerData.postCode,
    customerData.city,
    customerData.country,
  ]);

  const handleChangeReverseAlv = () => {
    setCustomerData((prev) => ({
      ...prev,
      reverseAlv: !prev.reverseAlv,
      ...(prev.reverseAlv
        ? {} // Do nothing when unchecked
        : {
            alvPercentage: 0,
          }),
    }));
  };

  const handleChangeCustomerType = (e) => {
    const { value } = e.target;
    switch (value) {
      case 'business':
        setCustomerData((prev) => ({
          ...prev,
          type: value,
        }));
        break;

      case 'other':
        setCustomerData((prev) => ({
          ...prev,
          type: value,
          customerBusinessId: '',
          billingType:
            prev.billingType === 'eInvoice' ? 'email' : prev.billingType,
          eInvoiceAddress: '',
          eInvoiceOperator: '',
        }));
        break;
      default:
        break;
    }
  };

  const handleSave = () => {
    const projectData = {
      ...customerData,
    };

    const filteredCustomerData = Object.entries(projectData).reduce(
      (acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    if (type === 'edit') {
      onUpdate(filteredCustomerData);
    } else {
      onCreate(filteredCustomerData);
    }
  };

  const { requiredFields, isSaveDisabled } = useMemo(() => {
    const requiredFields = { name: true };

    switch (customerData.type) {
      case 'business':
        requiredFields.customerBusinessId = true;
        requiredFields.customerVatId = true;
        break;
      default:
        break;
    }

    switch (customerData.billingType) {
      case 'email':
        requiredFields.billingEmail = true;
        break;
      case 'eInvoice':
        requiredFields.eInvoiceAddress = true;
        requiredFields.eInvoiceOperator = true;
        break;
      case 'mail':
        requiredFields.billingStreet1 = true;
        requiredFields.billingPostCode = true;
        requiredFields.billingCity = true;
        requiredFields.billingCountry = true;
        if (billingAddressSameAsContact) {
          requiredFields.street1 = true;
          requiredFields.postCode = true;
          requiredFields.city = true;
          requiredFields.country = true;
        }
        break;
    }

    const isSaveDisabled = Object.entries(requiredFields).some(
      ([key]) => !customerData[key]?.toString().trim()
    );

    return { requiredFields, isSaveDisabled };
  }, [customerData, billingAddressSameAsContact]);

  const isRequired = (name) => requiredFields[name] || false;

  const customerTypes = [
    { name: 'Yritys', value: 'business' },
    { name: 'Henkilöasiakas', value: 'other' },
  ];

  return (
    <Dialog
      aria-labelledby="project-dialog"
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick
      fullScreen={!upSm}
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
    >
      <DialogHeader
        icon={
          type === 'create' ? (
            <Icon path={mdiPlus} size={1} className={classes.icon} />
          ) : (
            <Icon path={mdiPencil} size={1} className={classes.icon} />
          )
        }
        onClose={onClose}
        title={type === 'create' ? 'Lisää uusi asiakas' : 'Muokkaa asiakasta'}
      />

      <DialogContent className={classes.dialogContent}>
        <ActionContainer // Asiakkaan tiedot
          title="Asiakkaan tiedot"
          required
        >
          <Grid container item spacing={1}>
            <Grid item xs={12} sm={8}>
              <TextInput
                label="Asiakkaan nimi"
                name="name"
                fullWidth
                value={customerData.name}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('name')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextInput
                label="Asiakasnumero"
                name="number"
                fullWidth
                value={customerData.number}
                onChange={handleChange}
                inputProps={{ maxLength: 30 }}
                disabled={loading}
                required={isRequired('number')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Asiakastyyppi"
                fullWidth
                name="type"
                value={customerData.type}
                onChange={handleChangeCustomerType}
                disabled={loading}
                select
                required={isRequired('type')}
              >
                {Object.entries(customerTypes).map(([key, value]) => (
                  <MenuItem key={key} value={value.value}>
                    {value.name}
                  </MenuItem>
                ))}
              </TextInput>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Y-tunnus"
                name="customerBusinessId"
                fullWidth
                value={customerData.customerBusinessId}
                onChange={handleChange}
                inputProps={{ maxLength: 30 }}
                disabled={loading || customerData.type !== 'business'}
                required={isRequired('customerBusinessId')}
              />
            </Grid>
            {upSm && <Grid item xs={12} sm={6}></Grid>}
            <Grid item xs={12} sm={6}>
              <TextInput
                label="ALV-tunnus"
                name="customerVatId"
                fullWidth
                value={customerData.customerVatId}
                onChange={handleChange}
                inputProps={{ maxLength: 30 }}
                disabled={loading || customerData.type !== 'business'}
                required={isRequired('customerVatId')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Sähköposti"
                name="email"
                fullWidth
                value={customerData.email}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('email')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Puhelinnumero"
                name="phone"
                fullWidth
                value={customerData.phone}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('phone')}
              />
            </Grid>
            <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
            <Grid item xs={12} sm={6}>
              <AddressAutocomplete
                label="Katuosoite"
                name="street1"
                value={customerData.street1}
                onChange={handleChange}
                handleSelect={handleChangeContactStreet1}
                disabled={loading}
                required={isRequired('street1')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label="Katuosoite 2"
                name="street2"
                fullWidth
                value={customerData.street2}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('street2')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label="Postilokero"
                name="poBox"
                fullWidth
                value={customerData.poBox}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('poBox')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label="C/O"
                name="co"
                fullWidth
                value={customerData.co}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('co')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label="Postinumero"
                name="postCode"
                fullWidth
                value={customerData.postCode}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('postCode')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label="Kaupunki"
                name="city"
                fullWidth
                value={customerData.city}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('city')}
              />
            </Grid>

            <Grid item xs={6}>
              <TextInput
                label="Maa"
                name="country"
                fullWidth
                value={customerData.country}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('country')}
              />
            </Grid>
            <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
            <Grid item xs={12} sm={12}>
              <TextInput
                label="Lisätiedot"
                name="additionalInfo"
                multiline
                minRows={3}
                fullWidth
                value={customerData.additionalInfo}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('additionalInfo')}
              />
            </Grid>
          </Grid>
        </ActionContainer>

        <ActionContainer // Yhteyshenkilö
          title="Yhteyshenkilö"
        >
          <Grid container item spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Etunimi"
                name="contactFirstName"
                value={customerData.contactFirstName}
                fullWidth
                onChange={handleChange}
                disabled={loading}
                required={isRequired('contactFirstName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Sukunimi"
                name="contactLastName"
                value={customerData.contactLastName}
                fullWidth
                onChange={handleChange}
                disabled={loading}
                required={isRequired('contactLastName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Yhteyshenkilön sähköposti"
                name="contactEmail"
                value={customerData.contactEmail}
                fullWidth
                onChange={handleChange}
                disabled={loading}
                required={isRequired('contactEmail')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Yhteyshenkilön puhelinnumero"
                name="contactPhone"
                value={customerData.contactPhone}
                fullWidth
                onChange={handleChange}
                disabled={loading}
                required={isRequired('contactPhone')}
              />
            </Grid>
          </Grid>
        </ActionContainer>
        <ActionContainer // Laskutustiedot
          title="Laskutustiedot"
          required
        >
          <Grid container item spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Laskutustapa"
                name="billingType"
                fullWidth
                value={customerData.billingType}
                onChange={handleChange}
                disabled={loading}
                select
              >
                <MenuItem value={'email'}>{'Sähköpostilasku'}</MenuItem>
                <MenuItem
                  value={'eInvoice'}
                  disabled={customerData.type !== 'business'}
                >
                  {'Verkkolasku'}
                </MenuItem>
                <MenuItem value={'mail'}>{'Kirjelasku'}</MenuItem>
              </TextInput>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Maksuehto"
                fullWidth
                name="paymentTerm"
                value={customerData.paymentTerm}
                onChange={handleChange}
                disabled={loading}
                renderValue={(selected) => selected}
                select
                required={isRequired('paymentTerm')}
              >
                {Object.entries(PAYMENT_TERM_OPTIONS).map(([key, value]) => (
                  <MenuItem key={key} value={value.value}>
                    {value.name}
                  </MenuItem>
                ))}
              </TextInput>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Viittenne"
                name="customerReferenceNumber"
                fullWidth
                value={customerData.customerReferenceNumber}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('customerReferenceNumber')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Laskun viesti"
                name="invoiceMessage"
                fullWidth
                value={customerData.invoiceMessage}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('invoiceMessage')}
              />
            </Grid>
            <Grid item xs={6}>
              <SwitchItem
                title="Käänteinen ALV"
                checked={customerData.reverseAlv}
                onChange={handleChangeReverseAlv}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label="ALV-prosentti"
                name="alvPercentage"
                fullWidth
                value={customerData.alvPercentage}
                onChange={handleChange}
                disabled={loading || customerData.reverseAlv}
                select
              >
                <MenuItem value={0}>{'0%'}</MenuItem>
                <MenuItem value={10}>{'10%'}</MenuItem>
                <MenuItem value={13.5}>{'13,5%'}</MenuItem>
                <MenuItem value={14}>{'14%'}</MenuItem>
                <MenuItem value={25.5}>{'25,5%'}</MenuItem>
              </TextInput>
            </Grid>
            {upSm && <Grid item xs={12} sm={6}></Grid>}
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Yleiskustannusprosentti"
                name="overheadPercentage"
                fullWidth
                value={customerData.overheadPercentage}
                onChange={handleChange}
                disabled={loading}
                required={isRequired('overheadPercentage')}
                type="number"
                adornment="%"
              />
            </Grid>

            <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
            {customerData.billingType === 'mail' && (
              <>
                <SwitchItem
                  title="Laskutusosoite sama kuin yrityksen osoite"
                  checked={billingAddressSameAsContact}
                  onChange={setBillingAddressSameAsContact}
                  disabled={loading}
                />
                <Grid item xs={12} sm={6}>
                  <AddressAutocomplete
                    label="Katuosoite"
                    name="billingStreet1"
                    value={customerData.billingStreet1}
                    onChange={handleChange}
                    handleSelect={handleChangeBillingStreet1}
                    disabled={loading || billingAddressSameAsContact}
                    required={isRequired('billingStreet1')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextInput
                    label="Katuosoite 2"
                    name="billingStreet2"
                    fullWidth
                    value={customerData.billingStreet2}
                    onChange={handleChange}
                    disabled={loading || billingAddressSameAsContact}
                    required={isRequired('billingStreet2')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextInput
                    label="Postilokero"
                    name="billingPoBox"
                    fullWidth
                    value={customerData.billingPoBox}
                    onChange={handleChange}
                    disabled={loading || billingAddressSameAsContact}
                    required={isRequired('billingPoBox')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextInput
                    label="C/O"
                    name="billingCo"
                    fullWidth
                    value={customerData.billingCo}
                    onChange={handleChange}
                    disabled={loading || billingAddressSameAsContact}
                    required={isRequired('billingCo')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextInput
                    label="Postinumero"
                    name="billingPostCode"
                    fullWidth
                    value={customerData.billingPostCode}
                    onChange={handleChange}
                    disabled={loading || billingAddressSameAsContact}
                    required={isRequired('billingPostCode')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextInput
                    label="Kaupunki"
                    name="billingCity"
                    fullWidth
                    value={customerData.billingCity}
                    onChange={handleChange}
                    disabled={loading || billingAddressSameAsContact}
                    required={isRequired('billingCity')}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextInput
                    label="Maa"
                    name="billingCountry"
                    fullWidth
                    value={customerData.billingCountry}
                    onChange={handleChange}
                    disabled={loading || billingAddressSameAsContact}
                    required={isRequired('billingCountry')}
                  />
                </Grid>
              </>
            )}
            {customerData.billingType === 'eInvoice' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    label="Verkkolaskuosoite"
                    name="eInvoiceAddress"
                    fullWidth
                    value={customerData.eInvoiceAddress}
                    onChange={handleChange}
                    disabled={loading}
                    required={isRequired('eInvoiceAddress')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    label="Operaattoritunnus"
                    fullWidth
                    name="eInvoiceOperator"
                    value={customerData.eInvoiceOperator}
                    onChange={handleChange}
                    disabled={loading}
                    renderValue={(selected) => selected} // Ensures only value is displayed in the input
                    select
                    required={isRequired('eInvoiceOperator')}
                  >
                    {Object.entries(E_INVOICE_OPERATORS).map(([key, value]) => (
                      <MenuItem key={key} value={value.value}>
                        {value.name} ({value.value})
                      </MenuItem>
                    ))}
                  </TextInput>
                </Grid>
              </>
            )}
            {customerData.billingType === 'email' && (
              <>
                <Grid item xs={12}>
                  <TextInput
                    label="Laskutussähköpostiosoite"
                    name="billingEmail"
                    fullWidth
                    value={customerData.billingEmail}
                    onChange={handleChange}
                    disabled={loading}
                    required={isRequired('billingEmail')}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </ActionContainer>
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Typography>
          {'Tähdellä (*) merkatut kentät ovat pakollisia'}
        </Typography>
        <Grid>
          {loading && <ButtonLoading />}
          <Button
            className={classes.actionBtn}
            onClick={() => handleSave()}
            color="dark"
            disabled={loading || isSaveDisabled}
          >
            {type === 'create' ? 'Lisää asiakas' : 'Tallenna'}
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

CustomerDialogLayout.defaultProps = {
  customer: {},

  type: 'create',
  loading: false,
};

CustomerDialogLayout.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  customer: PropTypes.object.isRequired,

  type: PropTypes.oneOf(['create', 'edit']),

  loading: PropTypes.bool,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default CustomerDialogLayout;
